/* eslint-disable no-unused-vars */
import React, {useEffect, useCallback} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik} from 'formik'

import {KTCard, KTCardBody, KTCardHeader, Select, Loading, HelpIcon} from '../../../components'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {
  projectLookup,
  buildingBlockTypeLookup,
  selectConstructionSitesByProject,
  selectBlocksByConstructionSite,
  resetProjectHierarchy,
  projectHierarchyLookup,
} from '../../../redux/slices/lookup.slice'
import {plotCreate} from '../../../redux/slices/project.slice'

import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs
} from '../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  projectLookupList: state.lookup.projectLookupList,
  buildingBlockTypeLookupList: state.lookup.buildingBlockTypeLookupList,
  loading: state.lookup.loading
})

const selectProjectData = (state) => ({
  isRedirect: state.project.isRedirect,
  loading: state.project.loading
})

function Create() {
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  
  const pageUrl = useMemoizedPageUrl(location)

  const {projectLookupList, buildingBlockTypeLookupList, loading} = useSelector(selectLookupData)
  const {isRedirect, loading: createLoading} = useSelector(selectProjectData)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(projectLookup())
  }, [dispatch])

  const validSchema = useMemoizedValidationSchema(() => Yup.object().shape({
    Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    ProjectId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    ConstructionSiteId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    BlockId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    BuildingBlockTypeId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    BasementHeight: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
  }), [intl])

  const formValues = useMemoizedFormValues({
    Name: '',
    ProjectId: '',
    ConstructionSiteId: '',
    BlockId: '',
    BuildingBlockTypeId: '',
    BasementHeight: '',
    Description: '',
  })

  const handleSubmit = useCallback(async (values, {setSubmitting, resetForm}) => {
    setSubmitting(true)
    dispatch(plotCreate(values))
    resetForm()
  }, [dispatch])

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'BC.PLOT'}),
      path: '/projects/plot',
      isSeparator: false,
      isActive: false,
    },
  ], [intl])

  const constructionSites = useSelector(selectConstructionSitesByProject(formik.values.ProjectId))
  const blocks = useSelector(selectBlocksByConstructionSite(formik.values.ProjectId, formik.values.ConstructionSiteId))

  useEffect(() => {
    if (isRedirect && isRedirect.id) {
      dispatch(resetProjectHierarchy())
      dispatch(projectHierarchyLookup())
      navigate(`/${pageUrl[1]}/${pageUrl[2]}/${pageUrl[3]}/detail/${isRedirect.id}`)
    }
  }, [isRedirect, navigate, pageUrl, dispatch])

  useEffect(() => {
    if (formik.values.ProjectId) {
      formik.setFieldValue('ConstructionSiteId', '')
      formik.setFieldValue('BlockId', '')
      formik.setFieldValue('BuildingBlockTypeId', '')
    }
  }, [formik.values.ProjectId])

  useEffect(() => {
    if (formik.values.ConstructionSiteId) {
      formik.setFieldValue('BlockId', '')
      formik.setFieldValue('BuildingBlockTypeId', '')
    }
  }, [formik.values.ConstructionSiteId])

  useEffect(() => {
    if (formik.values.BlockId) {
      dispatch(
        buildingBlockTypeLookup({
          projectId: formik.values.ProjectId,
        })
      )
      formik.setFieldValue('BuildingBlockTypeId', null)
    }
  }, [formik.values.BlockId])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>{intl.formatMessage({id: 'BC.PLOT.ADD'})}</PageTitle>
      <KTCard>
        <KTCardHeader title={intl.formatMessage({id: 'BC.PLOT.ADD'})}>
          <HelpIcon articleId='plot-create' />
        </KTCardHeader>
        <KTCardBody>
          <form className='form' onSubmit={formik.handleSubmit} noValidate>
            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'PLOT.SEARCH.NAME'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='text'
                  required
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('Name')}
                />
                {formik.touched.Name && formik.errors.Name && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.Name}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'PLOT.SEARCH.BASEMENTHEIGHT'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='text'
                  required
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('BasementHeight')}
                />
                {formik.touched.BasementHeight && formik.errors.BasementHeight && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.BasementHeight}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 required fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'PLOT.SEARCH.PROJECTNAME'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  data={projectLookupList}
                  required
                  {...formik.getFieldProps('ProjectId')}
                  onChange={(item) => formik.setFieldValue('ProjectId', Number(item?.value))}
                />
                {formik.touched.ProjectId && formik.errors.ProjectId && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.ProjectId}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {formik.values.ProjectId && (
              <>
                <div className='row mb-6'>
                  <label className='col-lg-3 required fw-bold fs-6 mb-2'>
                    {intl.formatMessage({id: 'PLOT.SEARCH.CONSTRUCTIONSITENAME'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <Select
                      required
                      data={constructionSites}
                      {...formik.getFieldProps('ConstructionSiteId')}
                      onChange={(item) =>
                        formik.setFieldValue('ConstructionSiteId', Number(item?.value))
                      }
                    />
                    {formik.touched.ConstructionSiteId && formik.errors.ConstructionSiteId && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{formik.errors.ConstructionSiteId}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {formik.values.ConstructionSiteId && (
                  <div className='row mb-6'>
                    <label className='col-lg-3 required fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'PLOT.SEARCH.BLOCKNAME'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <Select
                        required
                        data={blocks}
                        {...formik.getFieldProps('BlockId')}
                        onChange={(item) => formik.setFieldValue('BlockId', Number(item?.value))}
                      />
                      {formik.touched.BlockId && formik.errors.BlockId && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.BlockId}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {formik.values.BlockId && (
                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                      {intl.formatMessage({id: 'PLOT.SEARCH.BUILDINGBLOCKTYPENAME'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <Select
                        data={buildingBlockTypeLookupList}
                        {...formik.getFieldProps('BuildingBlockTypeId')}
                        onChange={(item) =>
                          formik.setFieldValue('BuildingBlockTypeId', Number(item?.value))
                        }
                      />
                    </div>
                  </div>
                )}
              </>
            )}

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'PLOT.SEARCH.DESCRIPTION'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <textarea
                  type='text'
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  rows={2}
                  {...formik.getFieldProps('Description')}
                />
              </div>
            </div>

            <div className='row pt-15'>
              <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                <button
                  className='btn btn-light'
                  onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}`)}
                >
                  {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                </button>
                <button
                  type='submit'
                  className='btn btn-primary'
                  data-kt-users-modal-action='submit'
                  disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                >
                  <span className='indicator-label'>
                    {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                  </span>
                  {formik.isSubmitting && (
                    <span className='indicator-progress'>
                      {intl.formatMessage({id: 'PLEASE'})}
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>
            </div>
          </form>
        </KTCardBody>
      </KTCard>

      {(loading | createLoading) > 0 && <Loading />}
    </>
  )
}
export {Create}
